'use client';

// Polyfill needed for Safari 15 support
import '@formatjs/intl-durationformat/polyfill.js';
import { useCurrentTime } from '@shared/hooks';
import { twMerge } from '@shared/themes';
import { useLocale } from 'next-intl';
import { type ComponentProps, useMemo, useState } from 'react';

type CountdownTwentyFourHoursProps = ComponentProps<'span'> & {
  timestamp: number;
  letterWidth?: string;
  /**
   * When set, the countdown stays hidden until the remaining time is at most this duration (ms).
   * e.g. `2 * 60 * 60 * 1000` shows the countdown only in the final 2 hours.
   */
  visibleTimeframe?: number;
  /**
   * When true, the countdown stays mounted at `0m 0s` after the timestamp is reached.
   */
  holdAtZero?: boolean;
};

const UPDATE_PERIOD = 200;
const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const ONE_HOUR = 60 * ONE_MINUTE;
const TWENTY_FOUR_HOURS = 24 * ONE_HOUR;

function isCountdownInWindow(remainingMs: number, visibleTimeframe?: number) {
  const withinTwentyFourHours = remainingMs > 0 && remainingMs <= TWENTY_FOUR_HOURS;
  const withinLotteryWindow = visibleTimeframe == null || remainingMs <= visibleTimeframe;
  return withinTwentyFourHours && withinLotteryWindow;
}

const HOUR_PLUS_CLOCK = { updatePeriod: ONE_SECOND, resolution: ONE_MINUTE } as const;
const SUB_HOUR_CLOCK = { updatePeriod: UPDATE_PERIOD, resolution: ONE_SECOND } as const;

/**
 * A component that displays a countdown timer when within 24 hours of a target date.
 * Renders digit characters in individual spans to avoid layout shift because of font inconsistency.
 *
 * @param {Object} props - Component props
 * @param {number} props.timestamp - Unix timestamp in milliseconds representing the target date
 * @param {string} [props.className] - Additional CSS classes to apply to the container
 * @param {string} [props.letterWidth] - Width of the letter spans - default is 6px, but for bigger font-sizes pass value accordingly
 * @param {number} [props.visibleTimeframe] - Optional max remaining duration (ms) before the countdown is shown
 * @param {boolean} [props.holdAtZero] - When true, the countdown stays mounted at `0m 0s` after the timestamp is reached.
 * @returns {JSX.Element | null} Countdown display or null if not within the visible window
 */
function CountdownTwentyFourHours({ timestamp, className, letterWidth = 'w-1.5', visibleTimeframe, holdAtZero = false, ...props }: CountdownTwentyFourHoursProps) {
  const locale = useLocale();
  const [useFineClock, setUseFineClock] = useState(false);
  const currentTime = useCurrentTime(useFineClock ? SUB_HOUR_CLOCK : HOUR_PLUS_CLOCK);
  const remainingMs = timestamp - currentTime;
  const nextUseFineClock = remainingMs <= ONE_HOUR;

  if (nextUseFineClock !== useFineClock) {
    setUseFineClock(nextUseFineClock);
  }

  const hoursMinutesFormatter = useMemo(
    () =>
      new Intl.DurationFormat(locale, {
        style: 'narrow',
        hoursDisplay: 'always',
        minutesDisplay: 'always',
      }),
    [locale]
  );
  const minutesSecondsFormatter = useMemo(
    () =>
      new Intl.DurationFormat(locale, {
        style: 'narrow',
        minutesDisplay: 'always',
        secondsDisplay: 'always',
      }),
    [locale]
  );

  const isExpired = remainingMs <= 0;

  if (!isCountdownInWindow(remainingMs, visibleTimeframe) && !(holdAtZero && isExpired)) {
    return null;
  }

  const totalSeconds = Math.max(0, Math.floor(remainingMs / ONE_SECOND));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = hours > 0 ? hoursMinutesFormatter.formatToParts({ hours, minutes }) : minutesSecondsFormatter.formatToParts({ minutes, seconds });
  const formattedDate = parts.map(part => part.value).join('');

  const classes = twMerge('flex items-center gap-x-0.25', className);

  return (
    <span className={classes} {...props}>
      {formattedDate.split('').map((char, index) => {
        const prevChar = formattedDate[index - 1] ?? '';
        const prevIsDigit = prevChar >= '0' && prevChar <= '9';
        // Narrow gap after hours (`1h 28m`); keep full letterWidth after minutes (`59m 49s`)
        const isNarrowSpace = char === ' ' && prevChar !== 'm';
        // Only the leading `1` of each unit group (`1h`, `12m`, `15s`) — not a trailing `1` (`21m`)
        const isLeadingOne = char === '1' && !prevIsDigit;

        return (
          <span className={twMerge(letterWidth, 'text-center', isNarrowSpace && 'w-0.75', isLeadingOne && 'w-1.1')} key={index}>
            {char}
          </span>
        );
      })}
    </span>
  );
}

export { CountdownTwentyFourHours, isCountdownInWindow, type CountdownTwentyFourHoursProps };
